import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Flex, Subtitle2 } from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';
import { MedFormIcon } from '@phx/myphx-lib';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import type { BasicPrescriberOrderFragment } from '../../../../util';
import { getProductDescription } from '../../../../util/formatting/prescriber-order/get-product-description';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

import { MedicationOrderDetails } from './MedicationOrderDetails';
import { MedicationOrderStateDisplay } from './MedicationOrderStateDisplay';
import { UnclaimedOrderInfoModal } from './UnclaimedOrderInfoModal';

export type MedicationOrderProps = {
    order: BasicPrescriberOrderFragment;
};

export const MedicationOrder = ({ order }: MedicationOrderProps) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const [
        showUnclaimedModal,
        { open: openUnclaimedModal, close: closeUnclaimedModal },
    ] = useDisclosure(false);
    const configOverride = useMedicationConfigOverride();

    const isUnclaimed = order.__typename === 'UnclaimedPrescriberOrder';

    const medication = getFragment(
        order.__typename === 'UnclaimedPrescriberOrder'
            ? order.prescribedMedicationUnclaimed
            : order.__typename === 'DigitallyReceivedPrescriberOrder'
              ? order.authorizedMedication
              : null
    );

    if (medication?.__typename == undefined) {
        return null;
    }

    const ndcKey =
        medication.__typename === 'UnknownMedication' && medication.originalNdc
            ? medication.originalNdc
            : medication.id;
    const medicationOverride = configOverride.ndcInfo[ndcKey];

    const medicationForm =
        medication.__typename === 'AuthorizedMedication'
            ? medication.formCode
            : undefined;

    const description = getProductDescription(order, i18n, medicationOverride);

    const fillOrder = () =>
        navigate(
            getAbsoluteRoute('order.prescriberOrderId.details', {
                prescriberOrderId: order.drxId ?? '',
            })
        );
    const clickHandler = isUnclaimed ? openUnclaimedModal : fillOrder;
    const verifyOrder = () =>
        navigate(
            getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                prescriberOrderId: order.drxId ?? '',
            })
        );

    return (
        <Flex gap="xs" justify="space-between" align="start" role="listitem">
            <Flex gap="xs">
                <MedFormIcon form={medicationForm} />
                <Box style={{ cursor: 'pointer' }} onClick={clickHandler}>
                    <Box data-testid="medication-entry-medication-description">
                        <Subtitle2>{description}</Subtitle2>
                    </Box>
                    <MedicationOrderDetails order={order} />
                </Box>
            </Flex>
            <MedicationOrderStateDisplay
                isUnclaimed={isUnclaimed}
                fillOrder={fillOrder}
                verifyOrder={verifyOrder}
            />
            <UnclaimedOrderInfoModal
                onClose={closeUnclaimedModal}
                verifyOrder={verifyOrder}
                visible={showUnclaimedModal}
            />
        </Flex>
    );
};
